import { parseGameBlueprint, type GameBlueprint } from "./blueprint";
import { parseCarcadeGameSpec, type CarcadeGameSpec } from "./game-spec";
import { buildGameBlueprintPrompt, buildSourceGamePrompt, buildSourceGameRepairPrompt } from "./kinds/shared/prompt";

export type GenerationPhase = "architect" | "builder" | "repair" | "critic" | "revision";

export interface ModelJsonRequest {
  readonly phase: GenerationPhase;
  readonly prompt: string;
  readonly temperature: number;
  readonly images?: readonly string[];
}

export interface ModelJsonResult {
  readonly candidate: unknown;
  readonly responseId?: string;
  readonly usage?: unknown;
}

export type ModelJsonRequester = (request: ModelJsonRequest) => Promise<ModelJsonResult>;

export interface GenerationPhaseReceipt {
  readonly phase: GenerationPhase;
  readonly responseId?: string;
  readonly usage?: unknown;
}

export interface GeneratedSourceGame {
  readonly spec: CarcadeGameSpec;
  readonly phases: readonly GenerationPhaseReceipt[];
}

function receipt(phase: GenerationPhase, result: ModelJsonResult): GenerationPhaseReceipt {
  return {
    phase,
    ...(result.responseId === undefined ? {} : { responseId: result.responseId }),
    ...(result.usage === undefined ? {} : { usage: result.usage }),
  };
}

function sameBlueprint(left: GameBlueprint, right: GameBlueprint): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function parseSourceGameForBlueprint(candidate: unknown, blueprint: GameBlueprint): CarcadeGameSpec {
  const spec = parseCarcadeGameSpec(candidate);
  if (spec.kind !== "source-game") throw new Error("builder must return a source-game");
  if (!sameBlueprint(spec.blueprint, blueprint)) throw new Error("builder changed the approved game blueprint");
  return spec;
}

export async function generateSourceGameWithModel(brief: string, requestJson: ModelJsonRequester): Promise<GeneratedSourceGame> {
  const architect = await requestJson({ phase: "architect", prompt: buildGameBlueprintPrompt(brief), temperature: 0.75 });
  const blueprint = parseGameBlueprint(architect.candidate);
  const builder = await requestJson({ phase: "builder", prompt: buildSourceGamePrompt(brief, blueprint), temperature: 0.55 });
  const phases: GenerationPhaseReceipt[] = [receipt("architect", architect), receipt("builder", builder)];
  try {
    return { spec: parseSourceGameForBlueprint(builder.candidate, blueprint), phases };
  } catch (error) {
    const message = error instanceof Error ? error.message : "invalid generated game";
    const repair = await requestJson({
      phase: "repair",
      prompt: buildSourceGameRepairPrompt(brief, blueprint, builder.candidate, message),
      temperature: 0.2,
    });
    phases.push(receipt("repair", repair));
    return { spec: parseSourceGameForBlueprint(repair.candidate, blueprint), phases };
  }
}
